import { Pencil, RefreshCw, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  POLICY_SYNC_INELIGIBLE_TOOLTIPS,
  type PolicySyncIneligibleReason,
} from "@shared/policy-sync-eligibility";
import { formatDate, formatLastUpdated } from "@/lib/formatters";
import { formatSignedMoney } from "@/lib/record-update-card";
import type { PolicySyncOutcome } from "@/lib/api";
import type { Policy } from "@shared/schema";
import { PolicyStatusBadge } from "./PolicyStatusBadge";
import { PolicySubstatusText } from "./PolicySubstatusText";
import {
  AOR_ALREADY_FLAGGED_TOOLTIP,
  AOR_FLAG_TOOLTIP,
  formatAorDate,
  isAorFlagged,
} from "./aor";
import {
  formatPolicyCancellationReason,
  formatPolicyPremium,
  shouldShowCancellationDate,
} from "./policy-status";

interface Props {
  policy: Policy;
  onEdit?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  syncing?: boolean;
  // CSR-1703: the latest terminal sync outcome. When it is "failed" or
  // "not_found", the "Updated …" impression is suppressed — DC's `lastSync`
  // can advance even on a not_found scrape (queue throttling), so the raw
  // timestamp alone can't be trusted to mean "the carrier confirmed this
  // data is fresh".
  // CSR-1977: "no_change" is the third case — the scrape DID reach the
  // carrier, so the timestamp is real and worth showing, but nothing was
  // updated, so the line says "Checked …" instead of "Updated …".
  // CSR-2174: "unattributable" is the fourth — the run finished and did not
  // fail, but DC would not say whether it describes THIS term, so the line
  // shows the timestamp without claiming it as an update.
  lastSyncOutcome?: PolicySyncOutcome | null;
  lastSyncError?: string | null;
  // CSR-1977: when that outcome was recorded, used to check it actually
  // describes the scrape whose timestamp we are about to label.
  lastSyncOutcomeAt?: string | null;
  // CSR-2169: why this policy cannot be synced (older term of its policy
  // number / carrier without a scraper), from the shared eligibility helper.
  // The button stays visible but disabled, with the approved reason tooltip.
  syncIneligibleReason?: PolicySyncIneligibleReason | null;
  // CSR-2327 — the AOR experience. `showAor` is the feature flag; the two
  // actions flag the policy "Customer AOR'd away" and clear it with "Access
  // restored". Status is untouched either way.
  showAor?: boolean;
  onFlagAor?: () => void;
  onRestoreAor?: () => void;
  aorPending?: boolean;
}

export function PolicyDetailHeader({
  policy,
  onEdit,
  onRefresh,
  refreshing,
  syncing,
  lastSyncOutcome,
  lastSyncError,
  lastSyncOutcomeAt,
  syncIneligibleReason,
  showAor = false,
  onFlagAor,
  onRestoreAor,
  aorPending = false,
}: Props) {
  const aorEnabled = showAor && !!onFlagAor && !!onRestoreAor;
  const aorFlagged = aorEnabled && isAorFlagged(policy);
  const subtitleParts = [policy.type, policy.carrier, policy.mga].filter(Boolean);
  // CSR-1079 — mirror the insured sidebar: prefer the real carrier-scrape
  // time (`carrierScrapedAt`), falling back to the AMS↔DC poll contact
  // (`syncedAt`) so the line never regresses to "Not yet synced".
  const lastUpdatedAt = policy.carrierScrapedAt ?? policy.syncedAt ?? null;
  const lastSyncFailed = lastSyncOutcome === "failed" || lastSyncOutcome === "not_found";
  const noChange =
    lastSyncOutcome === "no_change" &&
    outcomeDescribesScrape(lastSyncOutcomeAt, lastUpdatedAt);
  // CSR-2174 — the run finished without failing, but DC would not attribute the
  // result to this term. `Updated …` would be the same over-claim CSR-1977
  // guards against for `no_change`: the timestamp is real, what it proves is
  // not. Same staleness gate, for the same reason.
  const unconfirmedTerm =
    lastSyncOutcome === "unattributable" &&
    outcomeDescribesScrape(lastSyncOutcomeAt, lastUpdatedAt);
  // CSR-1984 — accounting-style premium consistent with the timeline cards and
  // modal overview: negatives render red as "($5,666.50)" with 2 decimals. A
  // blank premium keeps the "—" placeholder (no spurious $0.00).
  const premiumAmount = Number(policy.premium);
  const hasPremium =
    policy.premium != null &&
    policy.premium !== "" &&
    Number.isFinite(premiumAmount);
  const premiumIsNegative = hasPremium && premiumAmount < 0;

  // CSR-2169 — the button never disappears (AMS convention: disable and
  // guide). An ineligible policy keeps the visible button, disabled, with the
  // approved reason tooltip; an enabled button carries NO tooltip (tooltips
  // exist only to explain why a disabled button is disabled — per Ricardo).
  const syncDisabled = refreshing || !!syncIneligibleReason;
  const refreshControl = (
    <button
      type="button"
      onClick={onRefresh}
      disabled={syncDisabled}
      aria-label={syncing ? "Syncing policy" : "Refresh policy"}
      className={cn(
        "shrink-0 inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground transition-colors",
        syncDisabled
          ? "cursor-not-allowed opacity-60"
          : "hover:text-foreground cursor-pointer",
        // A disabled button swallows pointer/focus events, which would make
        // the reason tooltip unreachable — the focusable wrapper below is the
        // tooltip trigger instead, so events must pass through the button.
        syncIneligibleReason && "pointer-events-none",
      )}
      data-testid="button-refresh-policy"
    >
      <RefreshCw className={cn("w-3.5 h-3.5", syncing && "animate-spin")} />
    </button>
  );
  const refreshButton = onRefresh ? (
    syncIneligibleReason ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="inline-flex cursor-not-allowed"
              data-testid="refresh-policy-tooltip-target"
            >
              {refreshControl}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {POLICY_SYNC_INELIGIBLE_TOOLTIPS[syncIneligibleReason]}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      refreshControl
    )
  ) : null;

  // CSR-2327 — never disappears once the experience is on: a flagged policy
  // keeps the button, disabled, with a tooltip pointing at Access restored.
  const aorFlagTooltip = aorFlagged ? AOR_ALREADY_FLAGGED_TOOLTIP : AOR_FLAG_TOOLTIP;
  const flagAorButton = aorEnabled ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={aorFlagged ? 0 : -1} className="inline-flex">
            <button
              type="button"
              onClick={onFlagAor}
              disabled={aorFlagged || aorPending}
              aria-label={aorFlagTooltip}
              className={cn(
                "shrink-0 inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground transition-colors",
                aorFlagged || aorPending
                  ? "cursor-not-allowed opacity-60 pointer-events-none"
                  : "hover:text-pink-600 cursor-pointer",
              )}
              data-testid="button-flag-aor"
            >
              <ShieldOff className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{aorFlagTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <Card
      className={cn(
        "p-4 shadow-none",
        aorFlagged && "border-pink-300 dark:border-pink-800",
      )}
      data-testid="policy-detail-header"
    >
      {aorFlagged && (
        <div
          className="mb-3 flex flex-col gap-2 rounded-md border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-700 sm:flex-row sm:items-start sm:justify-between dark:border-pink-900 dark:bg-pink-950/40 dark:text-pink-300"
          data-testid="policy-aor-banner"
        >
          <div className="flex items-start gap-2 min-w-0">
            <ShieldOff className="mt-0.5 w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-semibold">
                Customer AOR'd away — this policy can no longer be accessed
              </p>
              <p>
                The automation lost access to this policy, so policy and customer
                data are no longer being updated. Identified on{" "}
                {formatAorDate(policy.aorIdentifiedAt)}.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onRestoreAor}
            disabled={aorPending}
            className="shrink-0 self-start font-semibold underline underline-offset-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="button-restore-aor"
          >
            Access restored
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p
              className="font-bold text-foreground"
              data-testid="policy-detail-number"
            >
              {policy.policyNumber}
            </p>
            {onEdit && (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-muted-foreground hover:text-foreground cursor-pointer"
                aria-label="Edit policy"
                data-testid="policy-detail-edit"
                onClick={onEdit}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {subtitleParts.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {subtitleParts.join(" · ")}
            </p>
          )}
          {refreshButton && (
            <div className="mt-1 flex items-center gap-1.5">
              <p
                className="text-xs text-muted-foreground"
                data-testid="policy-detail-last-updated"
                title={lastSyncFailed ? lastSyncError ?? undefined : undefined}
              >
                {syncing
                  ? "Fetching latest policy data..."
                  : lastSyncFailed
                    ? "Last sync did not complete — see details below"
                    : !lastUpdatedAt
                      ? "Not yet synced"
                      : noChange
                        ? `Checked ${formatLastUpdated(lastUpdatedAt)} — no changes`
                        : unconfirmedTerm
                          ? `Checked ${formatLastUpdated(lastUpdatedAt)} — not confirmed for this term`
                          : `Updated ${formatLastUpdated(lastUpdatedAt)}`}
              </p>
              {refreshButton}
              {flagAorButton}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="flex flex-col items-start gap-0.5" data-testid="policy-detail-status">
            <PolicyStatusBadge status={policy.status} />
            <PolicySubstatusText substatus={policy.substatus} />
          </span>
          <span
            className={cn(
              "font-semibold",
              premiumIsNegative ? "text-red-600" : "text-emerald-600",
            )}
            data-testid="policy-detail-premium"
          >
            {hasPremium ? formatSignedMoney(policy.premium) : "—"}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground">
        <HeaderField
          label="Effective"
          value={formatDate(policy.effectiveDate)}
          testId="policy-detail-effective"
        />
        <HeaderField
          label="Expiration"
          value={formatDate(policy.expirationDate)}
          testId="policy-detail-expiration"
        />
        {shouldShowCancellationDate(policy.status) && (
          <>
            <HeaderField
              label="Cancellation Date"
              value={formatDate(policy.cancellationDate ?? "")}
              testId="policy-detail-cancellation"
            />
            {/* CSR-1901 — carrier cancellation enrichment beside the date. The
                amount is the pay-to-keep figure, shown only for a non-payment
                cancellation; every field renders "—" when absent so the row's
                layout never shifts. */}
            <HeaderField
              label="Cancellation Reason"
              value={formatPolicyCancellationReason(policy)}
              testId="policy-detail-cancellation-reason"
            />
            <HeaderField
              label="Amount Due"
              value={
                policy.cancellationReason === "non_pay"
                  ? formatPolicyPremium(policy.amountDue)
                  : "—"
              }
              testId="policy-detail-amount-due"
            />
          </>
        )}
        <HeaderField label="Payment" value="—" />
        {aorFlagged && (
          <HeaderField
            label="AOR Identified"
            value={formatAorDate(policy.aorIdentifiedAt)}
            testId="policy-detail-aor-identified"
            className="text-pink-600 dark:text-pink-400"
          />
        )}
      </div>
    </Card>
  );
}

/**
 * CSR-1977 — does the recorded sync outcome actually describe the scrape whose
 * timestamp we are labelling?
 *
 * `lastSyncOutcome` comes from AMS's own activity trail, which only records
 * syncs AMS triggered. A later DC-initiated background scrape advances
 * `carrierScrapedAt` without leaving an outcome row behind, so an older
 * "no changes" result must not be pinned onto a newer scrape nobody watched —
 * that would state as fact something we never observed. Falling back to the
 * plain "Updated" wording is the safe direction: it claims less.
 *
 * An outcome with no timestamp is treated as describing the scrape, matching
 * how the CSR-1703 failure suppression already behaves.
 */
function outcomeDescribesScrape(
  outcomeAt: string | null | undefined,
  scrapedAt: Date | string | null,
): boolean {
  if (!outcomeAt || !scrapedAt) return true;
  const outcomeTime = new Date(outcomeAt).getTime();
  const scrapeTime = new Date(scrapedAt).getTime();
  if (isNaN(outcomeTime) || isNaN(scrapeTime)) return true;
  return outcomeTime >= scrapeTime;
}

function HeaderField({
  label,
  value,
  testId,
  className,
}: {
  label: string;
  value: string;
  testId?: string;
  className?: string;
}) {
  return (
    <span className={cn("min-w-0 break-words", className)} data-testid={testId}>
      {label}:{" "}
      <span className={cn("font-medium", className ? undefined : "text-foreground")}>
        {value}
      </span>
    </span>
  );
}
